import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormLabel from '@material-ui/core/FormLabel'
import Grid from '@material-ui/core/Grid'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import Typography from '@material-ui/core/Typography'
import DeleteIcon from '@material-ui/icons/Delete'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import DateTimePicker from '@mui/lab/DateTimePicker'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { FieldArray, Form, Formik, getIn } from 'formik'
import { isEqual } from 'lodash/fp'
import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DestinationPicker } from '../DestinationPicker'
import { DatePicker } from '../DatePicker'
import * as yup from 'yup'
import {
  amountFilter,
  dateToISO,
  dateToISOString,
  isoDateStrToDateOrNull,
  Locale,
  convertStringToBoolean,
} from '../../helpers'
import {
  combineAttachmentsList,
  getAttachmentsFromAttachmentListOrEmpty,
} from '../../models/attachmentListFnc'
import {
  AttachmentListRespType,
  AttachmentRespType,
  AttachmentType,
  CaseBARespType,
  CaseCommonDataType,
  CaseReportId,
  DamageType,
  DamageTypeEnum,
  ItemRespType,
  PartType,
} from '../../models/caseReport'
import { useStyles } from '../../pages/styled'
import { Attachments, AttachmentsChangedEvent } from '../Attachments'
import BanButton from '../BanButton'
import ButtonSimply from '../ButtonSimply'
import DateTextField from '../DateTextField'
import CalendarLogo from '../LogoCalendar'
import TextField from '../TextField'
import { CurrencyPicker } from '../CurrencyPicker'
import { LookupItemPicker } from '../LookupItemPicker'
import { LookupItemType } from '../../models'
import ErrorSubmitBack from '../ErrorSubmitBack'
import { Ga4Form } from './Ga4Form'
import { RadioButtonsField } from '../RadioButtonsField'
import { amountReplace, emptyStrToUndefined, objectToLookup, stringToNumber } from '../../helpers/miscFunctions'

/*
// typ dat, ze kterych komponenta plni inicialni data
// -- typy pochazeji z datoveho modelu - ze struktur Resp
//    - tedy z dat, ktera ziskavame ze serveru
interface BaggageFormInputDataType {
  caseCommon?: CaseCommonDataType;
  caseBA?: CaseBARespType;
}

// params ktere jsou vstupem teto komponenty
interface BaggageFormInputParamsType {
  data: BaggageFormInputDataType;
  isPageConfirmInProgress: boolean;
  errorMessage: string;
  OnPageConfirm (value: BaggageFormOutputDataType): void;
  OnPageBack (): void;
}

// datovy typ jako 'value' funkce 'OnPageConfirm'
// -- mapuje se do datoveho modelu na typy 'Params'
//    - tedy vstup do aktualizacnich metod na serveru
interface BaggageFormOutputDataType {
  caseCommon: CaseCommonDataType;
  caseBA: CaseBAParamsType;
}
*/

export interface BaggageFormInputDataType {
  caseCommon?: CaseCommonDataType | null
  caseBA?: CaseBARespType | null
}

export interface BaggageFormOutputDataType {
  caseCommon: CaseCommonDataType
  caseBA: CaseBARespType
}
export interface BaggageFormInputParamsType {
  caseReportId: CaseReportId
  data: BaggageFormInputDataType
  isPageConfirmInProgress: boolean
  errorMessage: string
  onPageContinue(value: BaggageFormOutputDataType): void
  onPageBack(): void
  notifyDataChanged(isChanged: boolean): void
}

const BaggageForm = (params: BaggageFormInputParamsType) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const lang = t('appl.header.language') === 'EN' ? 'en' : 'pl'

  // BACK
  const handleBackButtonClicked = () => {
    params.onPageBack()
  }

  // SUBMIT
  const handleFormikSubmit = (value: any) => {
    const modelValue: BaggageFormOutputDataType =
      transformFormikValuesToOutput(value)
    params.onPageContinue(modelValue)
  }

  // formik -> OUT
  function transformFormikValuesToOutput(values: any): BaggageFormOutputDataType {
    const caseCommonFromFormik: CaseCommonDataType = {
      // zde naplnis z formiku
      occurredOn: dateToISO(values.occurredOn),
      // typeof formik.destination === DestinationCode | null | undefined
      destination: values.destination,
      description: values.description,
      resort: values.resort,
      place: values.place,
    }

    const damageType: DamageType = values.damageType
    const editedAttachmentList: AttachmentListRespType[] = [
      // Porizene AttachmentListy
      // - jednotlive AttachmentListy jsou pri inicializaci formik
      //   initialValues budto sebrany z props.caseBA.attachmentList
      //   nebo vytvoreny nove prazdne. (Nasledujici hodnoty nejsou null|undefined:)
      {
        attachmentType: AttachmentType.PASS,
        attachments: values.attachmentsPASS,
      },
      {
        attachmentType: AttachmentType.TICKET,
        attachments: values.attachmentsTICKET,
      },
      {
        attachmentType: AttachmentType.PIR,
        attachments: values.attachmentsPIR,
      },
    ]
    const caseBAFromFormik: CaseBARespType = {
      // zde naplnis z formiku
      damageType: damageType,
      // baggageDateTime -- pouze, pokud je DELAY
      // TODO  v ISO -- example: 2022-01-02T12:24:25 DateTime
      baggageDateTime:
        damageType === 'DELAY'
          ? dateToISOString(values.baggageDateTime)
          : undefined,
      // TheftReported -- pouze, pokud je THEFT
      theftReported:
        damageType === 'THEFT'
          ? convertStringToBoolean(values.theftReported)
          : undefined,
      items: transformFormikItemsToOutput(damageType, values.items),
      attachmentList: combineAttachmentsList(
        params.data.caseBA?.attachmentList, // original
        editedAttachmentList // porizene
      ),
    }
    return {
      caseCommon: caseCommonFromFormik,
      caseBA: caseBAFromFormik,
    }
  }

  function transformFormikItemsToOutput(
    damageType: DamageType,
    itemsFormik: FormikItem[]
  ): ItemRespType[] {
    if (itemsFormik == null) {
      return []
    }

    return itemsFormik.map((item, index) => {
      const editedAttachmentList: AttachmentListRespType[] = [
        {
          attachmentType: AttachmentType.INVOICE,
          attachments: item.attachmentsINVOICE,
        },
        {
          attachmentType: AttachmentType.PHOTO,
          attachments: item.attachmentsPHOTO,
        },
      ]
      return {
        orderNum: index + 1,
        purchaseDate: dateToISO(item.purchaseDate),
        amount: stringToNumber(item.amount),
        currency: emptyStrToUndefined(item.currency),
        description: item.description,
        itemType: item.itemType,
        attachmentList: editedAttachmentList,
      }
    })
  }

  // IN -> FORMIK
  function transformInputToFormikValues(input: BaggageFormInputDataType): any {
    return {
      /* Date of damage */
      occurredOn: isoDateStrToDateOrNull(input?.caseCommon?.occurredOn),
      /* Country */
      destination: input?.caseCommon?.destination ?? null,
      resort: input?.caseCommon?.resort ?? '' /* Region */,
      place: input?.caseCommon?.place ?? '' /* Place */,
      damageType:
        input?.caseBA?.damageType ?? null /*  'LOSS' | `DELAY` | `THEFT` */,
      description: input?.caseCommon?.description ?? '',
      // TODO input je v iso stringu ISO DateTime -- formik ma byt DateTime objekt, ne?
      baggageDateTime: isoDateStrToDateOrNull(input?.caseBA?.baggageDateTime),
      theftReported: input?.caseBA?.theftReported?.toString(),
      attachmentsPASS: getAttachmentsFromAttachmentListOrEmpty(
        /* boarding pass */
        input?.caseBA?.attachmentList,
        AttachmentType.PASS
      ),
      attachmentsTICKET: getAttachmentsFromAttachmentListOrEmpty(
        /* luggage ticket */
        input?.caseBA?.attachmentList,
        AttachmentType.TICKET
      ),
      attachmentsREPORT: getAttachmentsFromAttachmentListOrEmpty(
        /* report */
        input?.caseBA?.attachmentList,
        AttachmentType.REPORT
      ),
      attachmentsPHOTO: getAttachmentsFromAttachmentListOrEmpty(
        /* photo */
        input?.caseBA?.attachmentList,
        AttachmentType.PHOTO
      ),
      attachmentsRECEIPT: getAttachmentsFromAttachmentListOrEmpty(
        /* receipt */
        input?.caseBA?.attachmentList,
        AttachmentType.RECEIPT
      ),
      attachmentsPIR: getAttachmentsFromAttachmentListOrEmpty(
        /* PIR report */
        input?.caseBA?.attachmentList,
        AttachmentType.PIR
      ),
      items: transformInputItemsToFormikValues(
        input.caseBA?.items ?? null
      ) /* list of items */,
    }
  }

  function transformInputItemsToFormikValues(
    items: ItemRespType[] | null
  ): any[] {
    if (items == null) {
      return []
    }

    return items.map((item: ItemRespType) => {
      return {
        purchaseDate: isoDateStrToDateOrNull(item.purchaseDate),
        amount: item.amount ?? '',
        currency: item.currency ?? null,
        description: item.description ?? '',
        itemType: item.itemType ?? null,
        attachmentsINVOICE: getAttachmentsFromAttachmentListOrEmpty(
          item.attachmentList,
          AttachmentType.INVOICE
        ),
        attachmentsPHOTO: getAttachmentsFromAttachmentListOrEmpty(
          item.attachmentList,
          AttachmentType.PHOTO
        ),
      }
    })
  }

  // Transformed model data to formik initial values
  const formikInitialValues = transformInputToFormikValues(params.data)

  type FormikItem = {
    itemType: string | null /* Type of the item */,
    description: string
    amount: string,
    currency: string | null,
    purchaseDate: Date | null,
    attachmentsINVOICE: AttachmentRespType[],
    attachmentsPHOTO: AttachmentRespType[],
  }

  const formikValueForNewItem: FormikItem = {
    itemType: null /* Type of the item */,
    description: '' /* Item */,
    amount: '',
    currency: null,
    purchaseDate: null,
    attachmentsINVOICE: [],
    attachmentsPHOTO: [],
  }
  // Fill in defaults: if there is no item, new empty item should be inserted

  if (
    formikInitialValues.items == null ||
    formikInitialValues.items.length === 0
  ) {
    formikInitialValues.items = [formikValueForNewItem]
  }

  const BaggageValidationSchema = () =>
    yup.object().shape({
      occurredOn: yup
        .date()
        .nullable()
        .required(t('baggageForm.occurredOn.required'))
        .min(new Date('1999-12-31'), t('dateMessage.err2'))
        .max(new Date(), t('dateMessage.err2'))
        .typeError(t('dateMessage.err1')),
      destination: yup
        .string()
        .required(t('baggageForm.destination.required'))
        .nullable(),
      place: yup.string().required(t('baggageForm.place.required')),
      resort: yup
        .string()
        .required(t('baggageForm.resort.required'))
        .test('len', t('baggageForm.resort.length'), (val: any) => {
          return val?.length <= 50
        }),
      damageType: yup
        .string()
        .nullable()
        .required(t('baggageForm.damageType.required')),
      description: yup.string(),
      baggageDateTime: yup
        .date()
        .nullable()
        .when('damageType', {
          is: (damageType: DamageType) => damageType === DamageTypeEnum.DELAY,
          then: yup
            .date()
            .nullable()
            .required(t('dateMessage.required'))
            .min(new Date('1999-12-31'), t('dateMessage.err2'))
            .max(new Date(), t('dateMessage.err2'))
            .typeError(t('dateMessage.err1')),
        }),
      theftReported: yup.boolean().nullable().when('damageType', {
        is: (damageType: DamageType | null | undefined) =>
          damageType === DamageTypeEnum.THEFT,
        then: yup.boolean().required(t('optionRequired')),
      }),
      attachmentsREPORT: yup
        .array()
        .nullable()
        .when('damageType', {
          is: (damageType: DamageType | null | undefined) =>
            damageType === DamageTypeEnum.LOSS ||
            damageType === DamageTypeEnum.THEFT,
          then: yup.array().nullable().min(1, t('uploadFile.err1')),
        }),
      attachmentsPASS: yup
        .array()
        .nullable()
        .when('damageType', {
          is: (damageType: DamageType | null | undefined) =>
            damageType === DamageTypeEnum.DELAY,
          then: yup.array().nullable().min(1, t('uploadFile.err1')),
        }),
      attachmentsTICKET: yup
        .array()
        .nullable()
        .when('damageType', {
          is: (damageType: DamageType | null | undefined) =>
            damageType === DamageTypeEnum.DELAY,
          then: yup.array().nullable().min(1, t('uploadFile.err1')),
        }),
      attachmentsPIR: yup
        .array()
        .nullable()
        .when('damageType', {
          is: (damageType: DamageType | null | undefined) =>
            damageType === DamageTypeEnum.DELAY,
          then: yup.array().nullable().min(1, t('uploadFile.err1')),
        }),
      items: yup.array().of(
        yup.object().shape({
          itemType: yup
            .string()
            .required(t(`baggageForm.items.itemType.required`))
            .nullable(),
          purchaseDate: yup
            .date()
            .nullable()
            .required(t('baggageForm.items.purchaseDate.required'))
            .min(new Date('1999-12-31'), t('dateMessage.err2'))
            .max(new Date(), t('dateMessage.err2'))
            .typeError(t('dateMessage.err1')),
          amount: yup
            .string()
            .required(t('amount.required'))
            .test(
              'numberFormat',
              t('message.err'),
              (val: string | undefined) => {
                const _number = Number(val)
                return !isNaN(_number)
              }
            )
            .test(
              'moreThan0',
              t('amount.moreThan0'),
              (val: string | undefined) => {
                const _number = Number(val)
                return _number > 0
              }
            ),
          currency: yup.string().nullable().required(t('currency.required')),
          description: yup
            .string()
            .required(t('baggageForm.items.description.required')),
          attachmentsPHOTO: yup
            .array()
            .nullable()
            .when('damageType', {
              is: (damageType: DamageType | null | undefined) =>
                damageType === DamageTypeEnum.DELAY,
              then: yup.array().nullable().min(1, t('uploadFile.err1')),
            }),
          attachmentsINVOICE: yup
            .array()
            .nullable()
            .min(1, t('uploadFile.err1')),
        })
      ),
    })

  const damageTypeLookup = useMemo(() =>
    objectToLookup(t('damageTypes', { returnObjects: true })).filter((item) =>
      [DamageTypeEnum.LOSS, DamageTypeEnum.DELAY, DamageTypeEnum.THEFT].includes(item.code as DamageTypeEnum)
    )
    , [t])
  const formikRef = useRef(null)
  return (
    <Ga4Form formName={'BaggageForm'} >
      <Formik
        initialValues={formikInitialValues}
        onSubmit={(values) => handleFormikSubmit(values)}
        validationSchema={BaggageValidationSchema}
        /* Enables re-setting of formik initialValues from props */
        enableReinitialize={true}
        innerRef={formikRef}
      >
        {({
          initialValues,
          values,
          errors,
          touched,
          setFieldValue,
          handleChange,
          handleBlur,
        }) => {
          const isChanged = isEqual(values, initialValues)
          params.notifyDataChanged(isChanged)
          function isError(name: string): boolean {
            return Boolean(
              getIn(errors, name) &&
              getIn(touched, name))
          }
          return (
            <Form className={classes.root}>
              <Grid
                item
                sm={12}
                xl={12}
                xs={12}
                container
                direction="row"
                className={classes.gridMainForm}
              >
                <Grid
                  item
                  sm={9}
                  xl={9}
                  xs={12}
                  container
                  direction="row"
                  className={classes.gridWorkArea}
                >
                  <Grid
                    item
                    sm={12}
                    xs={12}
                    xl={12}
                    container
                    className={classes.gridResize}
                  >
                    <Typography className={classes.text} variant="h3">
                      {t('baggageForm.title')}
                    </Typography>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={6} xs={6} sm={6}>
                        <DatePicker
                          label={t('baggageForm.occurredOn.label')}
                          name="occurredOn"
                          helperText={
                            errors.occurredOn && touched.occurredOn
                              ? errors.occurredOn
                              : null
                          }
                          error={Boolean(errors.occurredOn && touched.occurredOn)}
                          required={true}
                          value={values.occurredOn}
                          onChange={(value) => {
                            setFieldValue('occurredOn', value)
                          }}
                          minDate={new Date('2000, 0, 1')}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.fieldSize}
                      >
                        <DestinationPicker
                          name={'destination'}
                          label={t('baggageForm.destination.label')}
                          selectedItemCode={values.destination}
                          onValueChanged={(
                            selectedDestination: string | null
                          ) => {
                            setFieldValue('destination', selectedDestination)
                          }}
                          helperText={
                            errors.destination && touched.destination
                              ? errors.destination
                              : null
                          }
                          error={Boolean(
                            errors.destination && touched.destination
                          )}
                          required={true}
                          onBlur={handleBlur}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item sm={6} xl={6} xs={6}>
                        <TextField
                          name="resort"
                          label={t('baggageForm.resort.label')}
                          placeholder={t('baggageForm.resort.hint')}
                          error={!!(errors.resort && touched.resort)}
                          value={values.resort}
                          onChange={handleChange}
                          helperText={
                            errors.resort && touched.resort ? errors.resort : null
                          }
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                          onBlur={handleBlur}
                          required
                        />
                      </Grid>
                      <Grid item sm={6} xl={6} xs={6}>
                        <TextField
                          name="place"
                          label={t('baggageForm.place.label')}
                          placeholder={t('baggageForm.place.hint')}
                          error={!!(errors.place && touched.place)}
                          value={values.place}
                          onChange={handleChange}
                          helperText={
                            errors.place && touched.place ? errors.place : null
                          }
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                          onBlur={handleBlur}
                          inputProps={{
                            maxLength: 50,
                          }}
                          required
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <RadioButtonsField
                          aria-label={'damageType'}
                          name={'damageType'}
                          label={t('baggageForm.damageType.label')}
                          required
                          value={values.damageType}
                          onChange={handleChange}
                          lookup={damageTypeLookup}
                          error={Boolean(errors.damageType && touched.damageType)}
                          helperText={errors.damageType && touched.damageType
                            ? errors.damageType
                            : null}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.gridResize}
                      >
                        <TextField
                          aria-label="description"
                          name="description"
                          label={t('baggageForm.description.label')}
                          value={values.description}
                          onChange={handleChange}
                          multiline
                          rows={7}
                          rowsMax={7}
                          placeholder={t('baggageForm.description.hint')}
                          className={classes.textAreaClass}
                        />
                      </Grid>
                    </Grid>
                    {values.damageType === DamageTypeEnum.DELAY && (
                      <React.Fragment>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          xl={12}
                          container
                          className={classes.gridItem}
                          spacing={2}
                        >
                          <Grid
                            item
                            sm={12}
                            xl={12}
                            xs={12}
                            className={classes.fieldSize}
                          >
                            <LocalizationProvider
                              locale={lang === 'en' ? `` : Locale}
                              dateAdapter={AdapterDateFns}
                            >
                              <DateTimePicker
                                renderInput={(props) => (
                                  <DateTextField
                                    {...props}
                                    name="baggageDateTime"
                                    label={t('baggageForm.baggageDateTime.label')}
                                    error={
                                      errors.baggageDateTime &&
                                      touched.baggageDateTime
                                    }
                                    helperText={
                                      errors.baggageDateTime &&
                                        touched.baggageDateTime
                                        ? errors.baggageDateTime
                                        : null
                                    }
                                    className={classes.fieldSize}
                                    onBlur={handleBlur}
                                    required
                                  />
                                )}
                                inputFormat="dd.MM.yyyy HH:mm"
                                //@ts-ignore
                                autoOk="true"
                                margin="normal"
                                maxDate={new Date()}
                                minDate={new Date('2000, 0, 1')}
                                value={values.baggageDateTime}
                                onChange={(value) => {
                                  setFieldValue('baggageDateTime', value)
                                }}
                                InputProps={{ required: false }}
                                components={{ OpenPickerIcon: CalendarLogo }}
                                disableHighlightToday={true}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </Grid>
                        <Grid>
                          <Attachments
                            name={'attachmentsPASS'}
                            caseReportId={params.caseReportId}
                            labelText={t('uploadAttachments.CASEBA.PASS')}
                            attachmentType={AttachmentType.PASS}
                            part={PartType.CASEBA}
                            attachments={values.attachmentsPASS}
                            onAttachmentsChanged={(
                              event: AttachmentsChangedEvent
                            ) => {
                              setFieldValue(
                                'attachmentsPASS',
                                event.value.attachments
                              )
                            }}
                            onBlur={handleBlur}
                            required={true}
                            error={Boolean(
                              getIn(errors, 'attachmentsPASS') &&
                              getIn(touched, 'attachmentsPASS'))
                            }
                            helperText={
                              getIn(errors, 'attachmentsPASS') &&
                                getIn(touched, 'attachmentsPASS')
                                ? getIn(errors, 'attachmentsPASS')
                                : null
                            }
                          />
                        </Grid>
                        <Grid>
                          <Attachments
                            name={'attachmentsTICKET'}
                            caseReportId={params.caseReportId}
                            labelText={t('uploadAttachments.CASEBA.TICKET')}
                            attachmentType={AttachmentType.TICKET}
                            part={PartType.CASEBA}
                            //itemOrderNum=undefined
                            attachments={values.attachmentsTICKET}
                            onAttachmentsChanged={(
                              event: AttachmentsChangedEvent
                            ) => {
                              //console.log('onAttachmentsChanged: ', event)
                              // aktualizace values formiku
                              setFieldValue(
                                'attachmentsTICKET',
                                event.value.attachments
                              )
                            }}
                            onBlur={handleBlur}
                            required={true}
                            error={Boolean(
                              getIn(errors, 'attachmentsTICKET') &&
                              getIn(touched, 'attachmentsTICKET'))
                            }
                            helperText={
                              getIn(errors, 'attachmentsTICKET') &&
                                getIn(touched, 'attachmentsTICKET')
                                ? getIn(errors, 'attachmentsTICKET')
                                : null
                            }
                          />
                        </Grid>
                        <Grid>
                          <Attachments
                            name={'attachmentsPIR'}
                            caseReportId={params.caseReportId}
                            labelText={t('uploadAttachments.CASEBA.PIR')}
                            attachmentType={AttachmentType.PIR}
                            part={PartType.CASEBA}
                            attachments={values.attachmentsPIR}
                            onAttachmentsChanged={(
                              event: AttachmentsChangedEvent
                            ) => {
                              setFieldValue(
                                'attachmentsPIR',
                                event.value.attachments
                              )
                            }}
                            onBlur={handleBlur}
                            required={true}
                            error={Boolean(
                              getIn(errors, 'attachmentsPIR') &&
                              getIn(touched, 'attachmentsPIR'))
                            }
                            helperText={
                              getIn(errors, 'attachmentsPIR') &&
                                getIn(touched, 'attachmentsPIR')
                                ? getIn(errors, 'attachmentsPIR')
                                : null
                            }
                          />
                        </Grid>
                      </React.Fragment>
                    )}
                    {values.damageType === DamageTypeEnum.THEFT && (
                      <React.Fragment>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          xl={12}
                          container
                          className={classes.gridItem}
                          spacing={2}
                        >
                          <Grid
                            item
                            xl={12}
                            xs={12}
                            sm={12}
                            className={classes.row}
                          >
                            <FormControl component="fieldset">
                              <FormLabel
                                required
                                // @ts-ignore
                                error={
                                  errors.theftReported && touched.theftReported
                                }
                              >
                                {t('baggageForm.theftReported.label')}
                              </FormLabel>
                              <RadioGroup
                                aria-label="theftReported"
                                name="theftReported"
                                value={values.theftReported}
                                onChange={handleChange}
                                className={classes.row}
                              >
                                <FormControlLabel
                                  value="true"
                                  control={<Radio />}
                                  label={t('yes')}
                                />
                                <FormControlLabel
                                  value="false"
                                  control={<Radio />}
                                  label={t('no')}
                                />
                              </RadioGroup>
                              {errors.theftReported && touched.theftReported && (
                                <div className={classes.checkError}>
                                  {errors.theftReported}
                                </div>
                              )}
                            </FormControl>
                          </Grid>
                        </Grid>
                      </React.Fragment>
                    )}
                    {values.damageType === DamageTypeEnum.THEFT ||
                      values.damageType === DamageTypeEnum.LOSS ? (
                      <Grid>
                        <Attachments
                          name={'attachmentsREPORT'}
                          caseReportId={params.caseReportId}
                          labelText={t('uploadAttachments.CASEBA.REPORT')}
                          attachmentType={AttachmentType.REPORT}
                          part={PartType.CASEBA}
                          //itemOrderNum=undefined
                          attachments={values.attachmentsREPORT}
                          onAttachmentsChanged={(
                            event: AttachmentsChangedEvent
                          ) => {
                            //console.log('onAttachmentsChanged: ', event)
                            // aktualizace values formiku
                            setFieldValue(
                              'attachmentsREPORT',
                              event.value.attachments
                            )
                          }}
                          onBlur={handleBlur}
                          required={true}
                          error={Boolean(
                            getIn(errors, 'attachmentsREPORT') &&
                            getIn(touched, 'attachmentsREPORT'))
                          }
                          helperText={
                            getIn(errors, 'attachmentsREPORT') &&
                              getIn(touched, 'attachmentsREPORT')
                              ? getIn(errors, 'attachmentsREPORT')
                              : null
                          }
                        />
                      </Grid>
                    ) : (
                      <></>
                    )}
                    {values?.damageType !== null &&
                      values?.damageType !== undefined &&
                      values?.items && (
                        <FieldArray
                          name="items"
                          render={({ push, remove }) => (
                            <React.Fragment>
                              {values?.items &&
                                Boolean(values?.items.length) &&
                                values?.items.map((item: any, index: any) => {
                                  return (
                                    <React.Fragment key={index}>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Grid
                                          item
                                          xl={12}
                                          xs={12}
                                          sm={12}
                                          className={classes.rowAlign}
                                        >
                                          <Typography
                                            className={classes.text}
                                            variant="h3"
                                          >
                                            {index === 0
                                              ? t(
                                                `baggageForm.items.title0.${values.damageType}`
                                              )
                                              : t(
                                                `baggageForm.items.titleN.${values.damageType}`,
                                                { N: (index as number) + 1 }
                                              )}
                                          </Typography>
                                          {index ===
                                            (values?.items &&
                                              values?.items.length - 1) && (
                                              <BanButton
                                                onClick={() => remove(index)}
                                              >
                                                <DeleteIcon />
                                              </BanButton>
                                            )}
                                        </Grid>
                                        <Grid
                                          item
                                          sm={12}
                                          xl={12}
                                          xs={12}
                                          className={classes.fieldSize}
                                        >
                                          <LookupItemPicker
                                            name={`items.${index}.itemType`}
                                            label={t(
                                              `baggageForm.items.itemType.label.${values.damageType}`
                                            )}
                                            lookupItems={t<
                                              string,
                                              LookupItemType[]
                                            >(`itemTypes.${values.damageType}`, {
                                              defaultValue:
                                                [] /* returned if not found */,
                                              returnObjects:
                                                true /* array of {code:string, name:string} */,
                                            })}
                                            selectedItemCode={
                                              values.items[index].itemType ?? null
                                            }
                                            onValueChanged={(
                                              newValue: string | null
                                            ) => {
                                              setFieldValue(
                                                `items[${index}].itemType`,
                                                newValue
                                              )
                                            }}
                                            error={Boolean(
                                              getIn(
                                                errors,
                                                `items[${index}].itemType`
                                              ) &&
                                              getIn(
                                                touched,
                                                `items[${index}].itemType`
                                              ))
                                            }
                                            helperText={
                                              getIn(
                                                errors,
                                                `items[${index}].itemType`
                                              ) &&
                                                getIn(
                                                  touched,
                                                  `items[${index}].itemType`
                                                )
                                                ? getIn(
                                                  errors,
                                                  `items[${index}].itemType`
                                                )
                                                : null
                                            }
                                            required={true}
                                            onBlur={handleBlur}
                                          />
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <TextField
                                            label={t(`baggageForm.items.description.label.${values.damageType}`)}
                                            placeholder={t(
                                              `baggageForm.items.description.label.${values.damageType}`
                                            )}
                                            // name={`items.${index}.description`}
                                            value={
                                              values?.items[index]?.description
                                            }
                                            onChange={(e) => {
                                              setFieldValue(
                                                `items[${index}].description`,
                                                e.target.value
                                              )
                                            }}
                                            className={classes.fieldSize}
                                            InputProps={{ required: false }}
                                            inputProps={{
                                              maxLength: 50,
                                            }}
                                            onBlur={handleBlur}
                                            required
                                            error={isError(`items[${index}].description`)}
                                            helperText={isError(`items[${index}].description`) ?
                                              getIn(
                                                errors,
                                                `items[${index}].description`
                                              ) : null
                                            }
                                          />
                                        </Grid>
                                        <Grid item xl={6} xs={6} sm={6}>
                                          <DatePicker
                                            label={t(
                                              `baggageForm.items.purchaseDate.label.${values.damageType}`
                                            )}
                                            name={`items.${index}.purchaseDate`}
                                            error={Boolean(
                                              getIn(
                                                errors,
                                                `items[${index}].purchaseDate`
                                              ) &&
                                              getIn(
                                                touched,
                                                `items[${index}].purchaseDate`
                                              ))
                                            }
                                            helperText={
                                              getIn(
                                                errors,
                                                `items[${index}].purchaseDate`
                                              ) &&
                                                getIn(
                                                  touched,
                                                  `items[${index}].purchaseDate`
                                                )
                                                ? getIn(
                                                  errors,
                                                  `items[${index}].purchaseDate`
                                                )
                                                : null
                                            }
                                            required={true}
                                            value={
                                              values?.items[index]?.purchaseDate
                                            }
                                            onChange={(value) => {
                                              setFieldValue(
                                                `items[${index}].purchaseDate`,
                                                value
                                              )
                                            }}
                                            minDate={new Date('2000, 0, 1')}
                                          />
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <TextField
                                            name={`items[${index}].amount`}
                                            value={
                                              values?.items[index]?.amount ?? ''
                                            }
                                            label={t('baggageForm.items.amount.label')}
                                            required
                                            regex={amountFilter}
                                            inputProps={{ maxLength: 12 }}
                                            placeholder={t(
                                              'baggageForm.items.amount.hint'
                                            )}
                                            onChange={(e) => {
                                              setFieldValue(
                                                `items[${index}].amount`,
                                                amountReplace(e.target.value) ??
                                                ''
                                              )
                                            }}
                                            onBlur={handleBlur}
                                            error={isError(`items[${index}].amount`)}
                                            helperText={isError(`items[${index}].amount`)
                                              ? getIn(
                                                errors,
                                                `items[${index}].amount`
                                              )
                                              : null
                                            }
                                            className={classes.fieldSize}
                                            InputProps={{ required: false }}
                                          />
                                        </Grid>
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <CurrencyPicker
                                            name={`items[${index}].currency`}
                                            label={t('currency.label')}
                                            selectedItemCode={
                                              values.items[index].currency ?? null
                                            }
                                            onValueChanged={(
                                              selectedCurrencyCode: string | null
                                            ) => {
                                              setFieldValue(
                                                `items[${index}].currency`,
                                                selectedCurrencyCode
                                              )
                                            }}
                                            error={isError(`items[${index}].currency`)}
                                            helperText={isError(`items[${index}].currency`)
                                              ? getIn(
                                                errors,
                                                `items[${index}].currency`
                                              )
                                              : null
                                            }
                                            required={true}
                                            onBlur={handleBlur}
                                          />
                                        </Grid>
                                      </Grid>
                                      {values.damageType ===
                                        DamageTypeEnum.LOSS && (
                                          <>
                                            <Grid>
                                              <Attachments
                                                name={`items[${index}].attachmentsPHOTO`}
                                                caseReportId={params.caseReportId}
                                                labelText={t(
                                                  'uploadAttachments.ITEMBA.PHOTO'
                                                )}
                                                attachmentType={
                                                  AttachmentType.PHOTO
                                                }
                                                part={PartType.ITEMBA}
                                                itemOrderNum={index + 1}
                                                attachments={
                                                  values.items[index]
                                                    .attachmentsPHOTO
                                                }
                                                onAttachmentsChanged={(
                                                  event: AttachmentsChangedEvent
                                                ) => {
                                                  /*console.log(
                                                  'onAttachmentsChanged: ',
                                                  event
                                                )*/
                                                  // aktualizace values formiku
                                                  setFieldValue(
                                                    `items[${index}].attachmentsPHOTO`,
                                                    event.value.attachments
                                                  )
                                                }}
                                                onBlur={handleBlur}
                                                required={false}
                                                error={
                                                  getIn(
                                                    errors,
                                                    `items[${index}].attachmentsPHOTO`
                                                  ) &&
                                                  getIn(
                                                    touched,
                                                    `items[${index}].attachmentsPHOTO`
                                                  )
                                                }
                                                helperText={
                                                  getIn(
                                                    errors,
                                                    `items[${index}].attachmentsPHOTO`
                                                  ) &&
                                                    getIn(
                                                      touched,
                                                      `items[${index}].attachmentsPHOTO`
                                                    )
                                                    ? getIn(
                                                      errors,
                                                      `items[${index}].attachmentsPHOTO`
                                                    )
                                                    : null
                                                }
                                              />
                                            </Grid>
                                            <Grid>
                                              <Attachments
                                                name={`items[${index}].attachmentsINVOICE`}
                                                caseReportId={params.caseReportId}
                                                labelText={t(
                                                  'uploadAttachments.ITEMBA.INVOICE'
                                                )}
                                                attachmentType={
                                                  AttachmentType.INVOICE
                                                }
                                                part={PartType.ITEMBA}
                                                itemOrderNum={index + 1}
                                                attachments={
                                                  values.items[index]
                                                    .attachmentsINVOICE
                                                }
                                                onAttachmentsChanged={(
                                                  event: AttachmentsChangedEvent
                                                ) => {
                                                  setFieldValue(
                                                    `items[${index}].attachmentsINVOICE`,
                                                    event.value.attachments
                                                  )
                                                }}
                                                onBlur={handleBlur}
                                                required={true}
                                                error={Boolean(
                                                  getIn(
                                                    errors,
                                                    `items[${index}].attachmentsINVOICE`
                                                  ) &&
                                                  getIn(
                                                    touched,
                                                    `items[${index}].attachmentsINVOICE`
                                                  ))
                                                }
                                                helperText={
                                                  getIn(
                                                    errors,
                                                    `items[${index}].attachmentsINVOICE`
                                                  ) &&
                                                    getIn(
                                                      touched,
                                                      `items[${index}].attachmentsINVOICE`
                                                    )
                                                    ? getIn(
                                                      errors,
                                                      `items[${index}].attachmentsINVOICE`
                                                    )
                                                    : null
                                                }
                                              />
                                            </Grid>
                                          </>
                                        )}
                                      {values.damageType !==
                                        DamageTypeEnum.LOSS && (
                                          <Grid>
                                            <Attachments
                                              name={`items[${index}].attachmentsINVOICE`}
                                              caseReportId={params.caseReportId}
                                              labelText={t(
                                                'uploadAttachments.ITEMBA.INVOICE'
                                              )}
                                              attachmentType={
                                                AttachmentType.INVOICE
                                              }
                                              part={PartType.ITEMBA}
                                              itemOrderNum={index + 1}
                                              attachments={
                                                values.items[index]
                                                  .attachmentsINVOICE
                                              }
                                              onAttachmentsChanged={(
                                                event: AttachmentsChangedEvent
                                              ) => {
                                                setFieldValue(
                                                  `items[${index}].attachmentsINVOICE`,
                                                  event.value.attachments
                                                )
                                              }}
                                              onBlur={handleBlur}
                                              required={true}
                                              error={Boolean(
                                                getIn(
                                                  errors,
                                                  `items[${index}].attachmentsINVOICE`
                                                ) &&
                                                getIn(
                                                  touched,
                                                  `items[${index}].attachmentsINVOICE`
                                                ))
                                              }
                                              helperText={
                                                getIn(
                                                  errors,
                                                  `items[${index}].attachmentsINVOICE`
                                                ) &&
                                                  getIn(
                                                    touched,
                                                    `items[${index}].attachmentsINVOICE`
                                                  )
                                                  ? getIn(
                                                    errors,
                                                    `items[${index}].attachmentsINVOICE`
                                                  )
                                                  : null
                                              }
                                            />
                                          </Grid>
                                        )}
                                    </React.Fragment>
                                  )
                                })}
                              <Grid
                                item
                                xl={12}
                                xs={12}
                                sm={12}
                                className={classes.row}
                              >
                                <Grid
                                  item
                                  xl={6}
                                  xs={6}
                                  sm={6}
                                  className={classes.fieldSize}
                                >
                                  <ButtonSimply
                                    onClick={() => {
                                      const initialValue = {
                                        ...formikValueForNewItem,
                                      }
                                      push(initialValue)
                                    }}
                                  >
                                    {t(
                                      `baggageForm.items.addItem.${values.damageType}`
                                    )}
                                  </ButtonSimply>
                                </Grid>
                              </Grid>
                            </React.Fragment>
                          )}
                        />
                      )}
                    <ErrorSubmitBack
                      formik={formikRef.current}
                      backLinkText={t('baggageForm.backBtn')}
                      onBackLinkClick={handleBackButtonClicked}
                    >
                      {t('continueBtn')}
                    </ErrorSubmitBack>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Ga4Form >
  )
}

export default BaggageForm
